export type ApiErrorKind = 'down' | 'notFound' | 'server';

export class ApiError extends Error {
  readonly kind: ApiErrorKind;

  readonly status: number | null;

  constructor(kind: ApiErrorKind, message: string, status: number | null = null) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
  }
}

type QueryValue = string | number | undefined | null;

const DOWN_STATUSES = [502, 503, 504];

const buildUrl = (path: string, params?: Record<string, QueryValue>) => {
  const search = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  });
  const query = search.toString();
  return `/api${path}${query ? `?${query}` : ''}`;
};

// In mock mode only: forward ?mock=<scenario> from the page URL to the mock server.
const mockHeaders = (): Record<string, string> => {
  if (!__MOCK_API__) return {};
  const scenario = new URLSearchParams(window.location.search).get('mock');
  return scenario ? { 'x-mock-scenario': scenario } : {};
};

const readErrorMessage = async (response: Response) => {
  const text = await response.text().catch(() => '');
  try {
    const body = JSON.parse(text) as { error?: unknown };
    if (typeof body.error === 'string' && body.error) return body.error;
  } catch {
    // Not JSON (e.g. a text/plain 403). Fall through to the raw text.
  }
  return text.trim() || `The API answered with status ${response.status}.`;
};

const toApiError = async (response: Response) => {
  const message = await readErrorMessage(response);
  if (response.status === 404) return new ApiError('notFound', message, 404);
  if (DOWN_STATUSES.includes(response.status))
    return new ApiError('down', message, response.status);
  return new ApiError('server', message, response.status);
};

const get = async <T>(path: string, params?: Record<string, QueryValue>): Promise<{ data: T }> => {
  let response: Response;
  try {
    response = await fetch(buildUrl(path, params), {
      cache: 'no-store',
      headers: { accept: 'application/json', ...mockHeaders() },
    });
  } catch {
    throw new ApiError('down', 'Could not reach the dashboard API.');
  }
  if (!response.ok) throw await toApiError(response);
  // No strict runtime check: unknown fields pass through and are ignored by the UI.
  return { data: (await response.json()) as T };
};

export const apiClient = { get };
